import React, { useState, FormEvent } from 'react';
import apiService from '../../services/api';
import { useNavigate } from 'react-router-dom';

interface AdminLoginModalProps {
    isOpen: boolean;
    onClose: () => void;
    onLoginSuccess?: () => void;
}

const AdminLoginModal: React.FC<AdminLoginModalProps> = ({ isOpen, onClose, onLoginSuccess }) => {
    const navigate = useNavigate();
    const [formData, setFormData] = useState({
        email: '',
        password: '',
    });
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    // Form validation state
    const [touched, setTouched] = useState({
        email: false,
        password: false,
    });

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value } = e.target;
        setFormData((prev) => ({ ...prev, [name]: value }));
        setError(null);
    };

    const handleBlur = (field: keyof typeof touched) => {
        setTouched((prev) => ({ ...prev, [field]: true }));
    };

    const validateForm = (): boolean => {
        if (!formData.email.trim()) {
            setError('Email is required');
            return false;
        }

        // Validate email format
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(formData.email)) {
            setError('Please enter a valid email address');
            return false;
        }

        if (!formData.password) {
            setError('Password is required');
            return false;
        }

        return true;
    };

    const handleSubmit = async (e: FormEvent) => {
        e.preventDefault();

        // Mark all fields as touched
        setTouched({
            email: true,
            password: true,
        });

        // Validate form
        if (!validateForm()) {
            return;
        }

        // Clear any previous errors only after validation passes
        setError(null);
        setIsLoading(true);

        try {
            const response = await apiService.adminLogin(formData.email.trim(), formData.password);

            if (response.success && response.data) {
                console.log('Admin login successful:', response.message);

                // Store admin authentication data in sessionStorage
                if (response.data.authToken) {
                    sessionStorage.setItem('admin_auth_token', response.data.authToken);
                    sessionStorage.setItem('admin_token_type', response.data.tokenType);
                    sessionStorage.setItem('admin_token_expires_at', response.data.expiresAt);
                }

                // Store admin user info
                sessionStorage.setItem(
                    'admin_user',
                    JSON.stringify({
                        user_id: response.data.user.user_id,
                        user_name: response.data.user.user_name,
                        email: response.data.user.email,
                        permission_level: response.data.user.permission_level,
                    }),
                );

                console.log('Admin login completed successfully for:', {
                    user_name: response.data.user.user_name,
                    email: response.data.user.email,
                    permission_level: response.data.user.permission_level,
                    expires_at: response.data.expiresAt,
                });

                // Trigger authentication re-check and navigate to admin dashboard
                if (onLoginSuccess) {
                    onLoginSuccess();
                } else {
                    onClose();
                }
                navigate('/admin');
            } else {
                // Handle API returning success: false
                const errorMessage = response.message || response.error || 'Admin login failed. Please try again.';
                console.error('Admin login failed:', errorMessage);
                setError(errorMessage);
            }
        } catch (err: any) {
            console.error('Admin login error:', err);

            // Handle different error scenarios
            let errorMessage = 'An unexpected error occurred. Please try again.';

            if (err.response?.data) {
                // API returned an error response
                const errorData = err.response.data;
                if (errorData.message) {
                    errorMessage = errorData.message;
                } else if (errorData.error) {
                    errorMessage = typeof errorData.error === 'string' ? errorData.error : errorData.error.message;
                }
            } else if (err.message) {
                // Network or other error
                errorMessage = err.message;
            }

            setError(errorMessage);
        } finally {
            setIsLoading(false);
        }
    };

    const getFieldError = (field: keyof typeof touched): string | null => {
        if (!touched[field]) return null;

        switch (field) {
            case 'email':
                if (!formData.email.trim()) return 'Email is required';
                const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
                if (!emailRegex.test(formData.email)) return 'Please enter a valid email address';
                return null;
            case 'password':
                if (!formData.password) return 'Password is required';
                return null;
            default:
                return null;
        }
    };

    const handleClose = () => {
        // Reset form when closing
        setFormData({ email: '', password: '' });
        setTouched({ email: false, password: false });
        setError(null);
        onClose();
    };

    if (!isOpen) return null;

    return (
        <div
            className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[200]"
            onClick={handleClose}
            role="dialog"
            aria-modal="true"
            aria-labelledby="admin-login-title"
        >
            <div
                className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4 p-6"
                onClick={(e) => e.stopPropagation()}
            >
                <div className="flex justify-between items-center mb-6">
                    <h2 id="admin-login-title" className="text-2xl font-bold text-gray-900">
                        Admin Login
                    </h2>
                    <button
                        onClick={handleClose}
                        className="text-gray-400 hover:text-gray-600 transition-colors"
                        aria-label="Close modal"
                        disabled={isLoading}
                    >
                        <svg
                            className="w-6 h-6"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                            aria-hidden="true"
                        >
                            <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M6 18L18 6M6 6l12 12"
                            />
                        </svg>
                    </button>
                </div>

                <form onSubmit={handleSubmit} noValidate className="space-y-6">
                    {/* Email */}
                    <div>
                        <label htmlFor="admin-email" className="block text-sm font-semibold text-gray-700 mb-2">
                            Email Address <span className="text-red-500">*</span>
                        </label>
                        <input
                            type="email"
                            id="admin-email"
                            name="email"
                            value={formData.email}
                            onChange={handleInputChange}
                            onBlur={() => handleBlur('email')}
                            className={`w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 transition-all ${
                                getFieldError('email')
                                    ? 'border-red-500 focus:ring-red-500'
                                    : 'border-gray-300 focus:ring-blue-500'
                            }`}
                            placeholder="admin@example.com"
                            disabled={isLoading}
                            autoComplete="email"
                            required
                            aria-invalid={!!getFieldError('email')}
                            aria-describedby={getFieldError('email') ? 'admin-email-error' : undefined}
                        />
                        {getFieldError('email') && (
                            <p id="admin-email-error" className="text-red-500 text-sm mt-1">
                                {getFieldError('email')}
                            </p>
                        )}
                    </div>

                    {/* Password */}
                    <div>
                        <label htmlFor="admin-password" className="block text-sm font-semibold text-gray-700 mb-2">
                            Password <span className="text-red-500">*</span>
                        </label>
                        <input
                            type="password"
                            id="admin-password"
                            name="password"
                            value={formData.password}
                            onChange={handleInputChange}
                            onBlur={() => handleBlur('password')}
                            className={`w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 transition-all ${
                                getFieldError('password')
                                    ? 'border-red-500 focus:ring-red-500'
                                    : 'border-gray-300 focus:ring-blue-500'
                            }`}
                            placeholder="Enter your admin password"
                            disabled={isLoading}
                            autoComplete="current-password"
                            required
                            aria-invalid={!!getFieldError('password')}
                            aria-describedby={getFieldError('password') ? 'admin-password-error' : undefined}
                        />
                        {getFieldError('password') && (
                            <p id="admin-password-error" className="text-red-500 text-sm mt-1">
                                {getFieldError('password')}
                            </p>
                        )}
                    </div>

                    {/* Error Message */}
                    {error && (
                        <div className="p-3 bg-red-50 border border-red-200 rounded-lg" role="alert">
                            <p className="text-red-600 text-sm">{error}</p>
                        </div>
                    )}

                    {/* Submit Button */}
                    <button
                        type="submit"
                        disabled={isLoading}
                        className={`w-full py-3 px-4 rounded-lg font-semibold text-white transition-all ${
                            isLoading
                                ? 'bg-gray-400 cursor-not-allowed'
                                : 'bg-blue-600 hover:bg-blue-700 transform hover:scale-[1.02]'
                        }`}
                    >
                        {isLoading ? (
                            <span className="flex items-center justify-center">
                                <svg
                                    className="animate-spin -ml-1 mr-3 h-5 w-5 text-white"
                                    xmlns="http://www.w3.org/2000/svg"
                                    fill="none"
                                    viewBox="0 0 24 24"
                                    aria-hidden="true"
                                >
                                    <circle
                                        className="opacity-25"
                                        cx="12"
                                        cy="12"
                                        r="10"
                                        stroke="currentColor"
                                        strokeWidth="4"
                                    />
                                    <path
                                        className="opacity-75"
                                        fill="currentColor"
                                        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                                    />
                                </svg>
                                Signing in...
                            </span>
                        ) : (
                            'Sign In as Admin'
                        )}
                    </button>
                </form>
            </div>
        </div>
    );
};

export default AdminLoginModal;
